import { useEffect, useRef, useState } from "react";

// How long a card keeps its "went live" moment (CreativeCardParts.jsx: the bars' rise ends
// within about 900 ms; the magenta edge holds a little longer).
export const LIVE_MOMENT_MS = 1_400;

// How long a newly added card keeps its arrival class (its entrance is 280 ms).
export const ARRIVAL_MS = 600;

const quoted = (name) => name || "Your creative";

// Adds ids to a set for `ms`, then takes exactly those ids out again.
function flash(timers, setIds, ids, ms) {
  setIds((current) => new Set([...current, ...ids]));
  const timer = setTimeout(() => {
    timers.current.delete(timer);
    setIds((current) => {
      const next = new Set(current);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, ms);
  timers.current.add(timer);
}

// Watches the cards' phases across re-reads and reports the ones that just finished
// processing: `justLive` holds the ids that went live in this session (for the one-time
// animation, cleared after LIVE_MOMENT_MS), and `announcement` is the sentence a polite
// live region reads out, for a card that went live or failed. `justAdded` holds the ids
// that appeared in a re-read (a creative just made in the modal), for the card's arrival,
// cleared after ARRIVAL_MS. `cards` is null while the list is loading: the first read after
// that only records phases, so nothing announces or animates what was already there when
// the screen opened (or when it switched to another advertiser).
export function useCreativePhaseChanges(cards) {
  const previous = useRef(null);
  const timers = useRef(new Set());
  const [justLive, setJustLive] = useState(() => new Set());
  const [justAdded, setJustAdded] = useState(() => new Set());
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!cards) {
      previous.current = null;
      return;
    }
    const before = previous.current;
    previous.current = new Map(cards.map((c) => [c.id, c.phase]));
    if (!before) return;

    const added = cards.filter((c) => !before.has(c.id)).map((c) => c.id);
    if (added.length > 0) flash(timers, setJustAdded, added, ARRIVAL_MS);

    const finished = cards.filter((c) => before.get(c.id) === "processing" && c.phase !== "processing");
    const live = finished.filter((c) => c.phase === "active");
    const failed = finished.filter((c) => c.phase === "failed");
    if (live.length === 0 && failed.length === 0) return;

    setAnnouncement([
      ...live.map((c) => `${quoted(c.name)} is live.`),
      ...failed.map((c) => `${quoted(c.name)} couldn't be processed.`),
    ].join(" "));
    if (live.length === 0) return;

    flash(timers, setJustLive, live.map((c) => c.id), LIVE_MOMENT_MS);
  }, [cards]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  return { justLive, justAdded, announcement };
}
